import styled from '@emotion/styled';

export const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const PlansContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  margin-bottom: 48px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-bottom: 32px;
  }
`;

export const PlanCard = styled.div<{ isCurrentPlan?: boolean }>`
  background: var(--white);
  border: 1px solid ${props => (props.isCurrentPlan ? 'var(--prim4)' : 'var(--border1)')};
  border-radius: 12px;
  padding: 24px;
  position: relative;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  ${props =>
    props.isCurrentPlan &&
    `
    background: var(--prim4);
    color: var(--white);
  `}
`;

export const PlanHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

export const PlanBadge = styled.div`
  background: var(--white);
  color: var(--prim4);
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
`;

export const PlanPrice = styled.div`
  margin-bottom: 24px;
`;

export const PriceAmount = styled.span`
  font-size: 36px;
  font-weight: 700;
  line-height: 1;
`;

export const PriceUnit = styled.span`
  font-size: 16px;
  font-weight: 400;
  opacity: 0.8;
  margin-left: 4px;
`;

export const PlanFeatures = styled.div`
  margin-bottom: 24px;
`;

export const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-size: 14px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const BillingSection = styled.div`
  background: var(--white);
  border: 1px solid var(--border1);
  border-radius: 12px;
  padding: 24px;
`;

export const BillingHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

export const BillingTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const BillingRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr auto;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid var(--border1);
  align-items: center;

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr auto;
    gap: 8px;

    & > div:nth-of-type(2),
    & > div:nth-of-type(3) {
      display: none;
    }
  }
`;

export const StatusBadge = styled.span<{ status: 'completed' | 'pending' | 'failed' }>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;

  ${props => {
    switch (props.status) {
      case 'completed':
        return `
          background: #e8f5e8;
          color: #2d7d32;
        `;
      case 'pending':
        return `
          background: #fff3e0;
          color: #f57c00;
        `;
      case 'failed':
        return `
          background: #ffebee;
          color: #d32f2f;
        `;
      default:
        return `
          background: var(--neut7);
          color: var(--neut3);
        `;
    }
  }}
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: var(--neut3);
`;

// New pricing section styles converted from SCSS
export const PricingSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 56px;
`;

export const PricingCards = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  width: 100%;

  @media only screen and (min-width: 800px) {
    gap: 32px;
  }
`;

export const Card = styled.div<{ isCenter?: boolean }>`
  font-family: 'Poppins', sans-serif;
  flex: 1 1 300px;
  max-width: 100%;
  padding: 36px;
  border: 2px solid ${props => (props.isCenter ? 'var(--prim4)' : 'var(--border1)')};
  border-radius: 24px;
  background-color: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  position: relative;
`;

export const CardTitle = styled.p`
  font-size: 18px;
  line-height: 27px;
  font-weight: 600;
  margin-bottom: 4px;
  margin: 0 0 4px 0;
`;

export const CardDescription = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: var(--neut3);
  margin: 0;
`;

export const CardPriceContainer = styled.div`
  display: flex;
  align-items: baseline;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--border1);
  margin-bottom: 20px;
  margin-top: 20px;
`;

export const CardPrice = styled.p`
  font-size: 36px;
  font-weight: 600;
  margin: 0;
`;

export const CardPriceDescription = styled.p`
  font-size: 12px;
  font-weight: 600;
  color: var(--neut3);
  margin: 0;
`;

export const CardFeatures = styled.ul`
  list-style: none;
  margin: 24px 0 1.5rem 0;
  padding: 0;
  text-align: left;
  min-height: 250px;

  li {
    display: flex;
    align-items: center;
    font-size: 16px;
    margin-bottom: 12px;
    line-height: normal;

    svg {
      margin-right: 4px;
      min-height: 24px;
      min-width: 24px;
    }
  }
`;

export const CardTag = styled.div`
  background-color: #e6e2fe;
  border-radius: 32px;
  padding: 4px 12px;
  color: var(--prim4);
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-self: center;
  position: absolute;
  top: -11px;
  left: 50%;
  transform: translateX(-50%);
`;

export const ExpiryPill = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1;
`;

export const EmptyBillingContainer = styled.div`
  text-align: center;
  padding: 48px 24px;
  border: 1px solid var(--border1);
  border-radius: 4px;
`;
